import { toMetricNumber } from './normalize';
import { isExternalMetricV2AppId } from './kpi-marketplace-app-ids';
import {
  KpiWizardAppendContext,
  KpiWizardMetricPayload,
  LegacyIntegrationMetricData,
  WizardIntegrationSource,
} from './types';

/** v2 iframe apps: target from normalized metric `total` only. */
function resolveV2TargetFromSource(source: WizardIntegrationSource): number | undefined {
  return source.data.total;
}

/** Legacy append: total, then value fallback — both coerced to number. */
function resolveLegacyAppendTarget(
  metricData: LegacyIntegrationMetricData | undefined,
): number | undefined {
  const fromTotal = toMetricNumber(metricData?.total);
  if (fromTotal !== undefined) {
    return fromTotal;
  }
  return toMetricNumber(metricData?.value);
}

/** Legacy MIT child KR: bitmask merge — callers must supply numeric permission_teams. */
function mergeMitPermissionTeams(
  itemTeams: number | number[] | undefined,
  goalTeams: number | undefined,
): number {
  // eslint-disable-next-line no-bitwise
  return Number(itemTeams) | Number(goalTeams);
}

export function appendInitialData(
  payload: KpiWizardMetricPayload,
  context: KpiWizardAppendContext,
): KpiWizardMetricPayload {
  let initialData: number | string = 0;
  if (context.item?.initial_data != null && context.item.initial_data !== '') {
    initialData = context.item.initial_data;
  }
  return {
    ...payload,
    initial_data: initialData,
  };
}

export function appendLastSourceTypeData(payload: KpiWizardMetricPayload): KpiWizardMetricPayload {
  return {
    ...payload,
    retainUpdates: 2,
  };
}

export function appendAddOkrData(
  payload: KpiWizardMetricPayload,
  context: KpiWizardAppendContext,
): KpiWizardMetricPayload {
  const goalDetails = context.addOkrData?.goalDetails;
  if (!goalDetails) {
    return payload;
  }
  return {
    ...payload,
    permission_teams: goalDetails.permission_teams,
    permission_biz: goalDetails.permission_biz,
  };
}

export function appendItemData(
  payload: KpiWizardMetricPayload,
  integrationSource: WizardIntegrationSource | undefined,
  context: KpiWizardAppendContext,
): KpiWizardMetricPayload {
  const { item } = context;
  if (!item) {
    return payload;
  }

  const itemTarget = toMetricNumber(item.target);
  const hasSavedTarget = item.target !== undefined && item.target !== null && item.target !== '';
  const extAppId = Number(item.ext_app_id || payload.ext_app_id);

  let dataSourceTarget: number | undefined;
  if (isExternalMetricV2AppId(extAppId) && integrationSource) {
    dataSourceTarget = resolveV2TargetFromSource(integrationSource);
  } else if (!isExternalMetricV2AppId(extAppId)) {
    const legacyMetricData = integrationSource?.data as LegacyIntegrationMetricData | undefined;
    dataSourceTarget = resolveLegacyAppendTarget(legacyMetricData);
  }

  let target: number | undefined;
  if (!hasSavedTarget) {
    target = payload.target ?? dataSourceTarget;
  } else {
    target = itemTarget;
  }

  let result: KpiWizardMetricPayload = {
    ...payload,
    id: item.id,
    type: item.type,
    target,
    permission_teams: item.permission_teams,
    permission_biz: item.permission_biz,
    permission: item.permission,
    metric: item.metric,
    progress_type: item.progress_type,
    progress_consider: item.progress_consider,
  };

  if (item.interval == 2 || item.interval == 6) {
    result.weekly_update_day = item.weekly_update_day;
  }

  if (context.isChildKr && context.fromParentKr) {
    result = {
      ...result,
      interval: context.basicInlineData?.interval || item.interval,
      start_date: context.basicInlineData?.duration?.start || item.duration?.start,
      end_date: context.basicInlineData?.duration?.end || item.duration?.end,
      parent_goal_id: context.goalDetails?.id,
      metric: context.basicInlineData?.metric || item.metric,
      weekly_update_day: context.basicInlineData?.weekly_update_day || item.weekly_update_day,
    };
  }

  if (context.mitChildKr) {
    const metricData = integrationSource?.data as LegacyIntegrationMetricData | undefined;
    let mitProgressType: number | string = 1;
    if (item.progress_type != null) {
      mitProgressType = item.progress_type;
    } else if (context.mitType && context.getProgressType) {
      mitProgressType = context.getProgressType(context.mitType);
    }
    const mitIndex = Number(context.mitIndex ?? 0);
    const mitTarget = toMetricNumber(item.target) ?? toMetricNumber(metricData?.total) ?? 0;
    result = {
      ...result,
      parent_goal_id: context.goalDetails?.id,
      metric: item.metric || 1,
      end_date: item.end_date,
      start_date: item.start_date,
      quarter_name: `Q${mitIndex + 1}`,
      progress_type: mitProgressType,
      counting: item.counting,
      type: item.type || context.goalDetails?.type,
      target: mitTarget,
      permission_teams: mergeMitPermissionTeams(
        item.permission_teams,
        context.goalDetails?.permission_teams,
      ),
      permission_biz: item.permission_biz || context.goalDetails?.permission_biz,
      permission: item.permission || context.goalDetails?.permission,
      owner_pic: item.owner_pic || context.goalDetails?.owner_pic,
      real_owner_pic: item.real_owner_pic || context.goalDetails?.owner_pic,
      totalTargetText: mitTarget,
    };
  }

  return result;
}
